'use client';

import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { CalendarIcon } from '@radix-ui/react-icons';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  cn,
  dateTimeFormatWithoutSeconds,
  formatDateTime,
} from '@ais-ui/utils';
import { Calendar } from './Calendar';
import { Input } from './Input';
import { Label } from './Label';
import { Separator } from './Separator';
import { clampTimeValue } from '../../../../apps/ais/src/modules/utils/clampTimeValue';

// Define the props for the component
interface DateTimePickerProps {
  date: Date | undefined;
  onChange: (value: Date | undefined) => void;
  timeZone: string;
  dateTimeFormat: string;
  dateLimit: 'past' | 'future' | 'all';
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

const setTimeToNow = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return;
  }

  const now = new Date();
  const zonedNow = utcToZonedTime(now, timeZone);

  localDate.setTime(zonedNow.getTime());
};

const setTimeToMidday = (localDate: Date | undefined) => {
  if (!localDate) {
    return;
  }

  // Set the time to midday (12:00 PM) in local time
  localDate.setHours(12, 0, 0, 0);
};

const isFutureDatetime = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return false;
  }
  const dateInUtc = zonedTimeToUtc(localDate, timeZone);
  const now = new Date();
  return dateInUtc > now;
};

const isPastDatetime = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return false;
  }

  const dateInUtc = zonedTimeToUtc(localDate, timeZone);
  const now = new Date();

  return dateInUtc < now;
};

const isToday = (localDate: Date | undefined, timeZone: string) => {
  if (!localDate) {
    return false;
  }

  // Convert the current UTC date to the given timezone's local date
  const nowInTimeZone = utcToZonedTime(new Date(), timeZone);

  // Extract the date parts for comparison
  const year = nowInTimeZone.getFullYear();
  const month = nowInTimeZone.getMonth();
  const day = nowInTimeZone.getDate();

  // Convert the localDate to the given timezone's local date
  const dateInTimeZone = utcToZonedTime(localDate, timeZone);

  // Compare if the year, month, and date match
  return (
    dateInTimeZone.getFullYear() === year &&
    dateInTimeZone.getMonth() === month &&
    dateInTimeZone.getDate() === day
  );
};

export const DateTimePicker = ({
  className,
  date,
  onChange,
  timeZone = 'UTC',
  dateTimeFormat,
  dateLimit = 'all',
  disabled = false,
  minDate,
  maxDate,
}: DateTimePickerProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const localDate = useMemo(
    () => (date ? utcToZonedTime(date, timeZone) : undefined),
    [date, timeZone]
  );

  const handleDateSelection = useCallback(
    (selectedDate: Date | undefined) => {
      if (!selectedDate) {
        onChange(undefined);
        return;
      }

      // If we already have a date, preserve the time
      if (localDate) {
        selectedDate.setHours(
          localDate.getHours(),
          localDate.getMinutes(),
          0,
          0
        );
      }

      if (dateLimit === 'past') {
        // Make sure past times don't go into the future
        if (isFutureDatetime(selectedDate, timeZone)) {
          setTimeToNow(selectedDate, timeZone);
        }

        if (!date && isToday(selectedDate, timeZone)) {
          setTimeToNow(selectedDate, timeZone);
        }

        if (
          !date &&
          isPastDatetime(selectedDate, timeZone) &&
          !isToday(selectedDate, timeZone)
        ) {
          setTimeToMidday(selectedDate);
        }
      } else if (dateLimit === 'future') {
        if (isPastDatetime(selectedDate, timeZone)) {
          setTimeToNow(selectedDate, timeZone);
        }

        if (!date && isToday(selectedDate, timeZone)) {
          setTimeToNow(selectedDate, timeZone);
        }

        if (!date && isFutureDatetime(selectedDate, timeZone)) {
          setTimeToMidday(selectedDate);
        }
      }

      onChange(zonedTimeToUtc(selectedDate, timeZone));
    },
    [date, localDate, timeZone, onChange, dateLimit]
  );

  const disableDate = useCallback(
    (zonedDateToCheck: Date) => {
      const today = new Date();
      const zonedToday = utcToZonedTime(today, timeZone);
      zonedToday.setHours(0, 0, 0, 0);

      if (minDate && zonedDateToCheck < minDate) {
        return true;
      }
      if (maxDate && zonedDateToCheck > maxDate) {
        return true;
      }

      switch (dateLimit) {
        case 'past':
          return zonedDateToCheck > zonedToday;
        case 'future':
          return zonedDateToCheck < zonedToday;
        case 'all':
        default:
          return false;
      }
    },
    [timeZone, minDate, maxDate, dateLimit]
  );

  const formattedDate = useMemo(() => {
    if (!localDate) {
      return 'Pick a date and time';
    }
    return formatDateTime(
      localDate,
      dateTimeFormatWithoutSeconds(dateTimeFormat)
    );
  }, [localDate, dateTimeFormat]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const fromYear = useMemo(() => currentYear - 5, [currentYear]);
  const toYear = useMemo(() => currentYear + 2, [currentYear]);

  const onChangeTime = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'minutes' | 'hours') => {
      const value = e.target.value.trim();
      if (!value) {
        return;
      }

      if (!localDate) {
        // If no existing date, create a new one for today
        const newDate = new Date();
        const zonedDate = utcToZonedTime(newDate, timeZone);

        const numberValue = clampTimeValue(parseInt(value, 10), type);

        if (type === 'minutes') {
          zonedDate.setMinutes(numberValue);
        } else if (type === 'hours') {
          zonedDate.setHours(numberValue);
        }

        onChange(zonedTimeToUtc(zonedDate, timeZone));
      } else {
        const numberValue = clampTimeValue(parseInt(value, 10), type);
        const updatedLocalDate = new Date(localDate);

        if (type === 'minutes') {
          updatedLocalDate.setMinutes(numberValue);
        } else if (type === 'hours') {
          updatedLocalDate.setHours(numberValue);
        }

        onChange(zonedTimeToUtc(updatedLocalDate, timeZone));
      }
    },
    [localDate, onChange, timeZone]
  );

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !localDate && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formattedDate}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 flex flex-col" align="start">
          <Calendar
            disabled={(date) => disableDate(date)}
            initialFocus
            mode="single"
            captionLayout="dropdown-buttons"
            defaultMonth={localDate}
            selected={localDate}
            onSelect={handleDateSelection}
            numberOfMonths={1}
            fromYear={fromYear}
            toYear={toYear}
          />
          <Separator />

          <div className="p-4">
            <div className="flex items-center space-x-2">
              <Input
                value={
                  localDate ? String(localDate.getHours()).padStart(2, '0') : ''
                }
                id="hours"
                placeholder="HH"
                type="number"
                onChange={(e) => onChangeTime(e, 'hours')}
                className="h-8 w-[70px]"
              />
              <Label> : </Label>
              <Input
                value={
                  localDate
                    ? String(localDate.getMinutes()).padStart(2, '0')
                    : ''
                }
                id="minutes"
                placeholder="MM"
                type="number"
                onChange={(e) => onChangeTime(e, 'minutes')}
                className="h-8 w-[70px]"
              />
            </div>
          </div>

          <Separator />

          <div className="space-x-2 p-4">
            <Label className="bold text-primary">Selected:</Label>
            <Label className="text-muted-foreground">{formattedDate}</Label>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
